import { compact } from '@quromedical/utils';

import { valsOrNone } from '../array';
import { CHARGE_ITEM_DEFINITION_APPLICABILITY_LANGUAGE } from '../coding';

type ComparatorName = 'equal';

/**
 * For now we only support equality expressions but we could easily add support for other types of expressions
 */
type ChargeItemExpression<TKey extends string = string> = `${TKey}=${string}`;

const createChargeItemDefinitionApplicabilityExpression = <TKey extends string>(
  key: TKey,
  value: string,
): ChargeItemExpression<TKey> => `${key}=${value}`;

export interface ChargeItemDefinitionApplicability<TKey extends string>
  extends fhir4.ChargeItemDefinitionApplicability {
  language: typeof CHARGE_ITEM_DEFINITION_APPLICABILITY_LANGUAGE;
  expression: ChargeItemExpression<TKey>;
}

const createChargeItemDefinitionApplicabilityItem = <TKey extends string>(
  key: TKey,
  value: string,
  description?: string,
): ChargeItemDefinitionApplicability<TKey> => ({
  description,
  language: CHARGE_ITEM_DEFINITION_APPLICABILITY_LANGUAGE,
  expression: createChargeItemDefinitionApplicabilityExpression(key, value),
});

export type ChargeItemDefinitionApplicabilityList<TKey extends string> =
  ChargeItemDefinitionApplicability<TKey>[];

export type ChargeItemDefinitionApplicabilityCondition = {
  type: ComparatorName;
  value: string;
  description?: string;
};

export type ChargeItemDefinitionApplicabilityConditions<
  TKey extends string = string,
> = Record<TKey, ChargeItemDefinitionApplicabilityCondition>;

/**
 * Convert an object that defines the required values for a charge item applicability into
 * a set of expressions that need to be met
 */
export const createChargeItemDefinitionApplicability = <
  TKey extends string,
  TConditions extends ChargeItemDefinitionApplicabilityConditions<TKey>,
>(
  conditions: Partial<TConditions>,
): ChargeItemDefinitionApplicabilityList<TKey> | undefined => {
  const result = Object.keys(conditions).map((key) => {
    const condition = conditions[key as TKey];
    if (!condition) {
      return undefined;
    }

    return createChargeItemDefinitionApplicabilityItem(
      key as TKey,
      condition.value,
      condition.description,
    );
  });

  return valsOrNone(compact(result));
};

const isDefined = <T>(value?: T): value is T => !!value;

const parseExpression = (
  expression = '',
): [key: string, value: string] | undefined => {
  const [key, value] = expression.split('=');

  const isValid = isDefined(key) && isDefined(value);
  if (!isValid) {
    return undefined;
  }

  return [key, value];
};

/**
 * Parse Applicability expression if it is valid
 *
 * @returns the parsed expression or `undefined`
 */
const parseApplicabilityExpression = <TKey extends string>(
  applicability: fhir4.ChargeItemDefinitionApplicability,
):
  | [key: TKey, value: ChargeItemDefinitionApplicabilityCondition]
  | undefined => {
  const { language, description, expression } = applicability;

  if (language !== CHARGE_ITEM_DEFINITION_APPLICABILITY_LANGUAGE) {
    return undefined;
  }

  const parsed = parseExpression(expression);

  if (!parsed) {
    return undefined;
  }

  const [key, value] = parsed;

  const condition: ChargeItemDefinitionApplicabilityCondition = {
    type: 'equal',
    value,
    description,
  };

  return [key as TKey, condition];
};

/**
 * Converts an applicability list into the relevant charge item condition provided that the
 * applicability is a valid ChargeItemExpression
 */
export const getChargeItemDefinitionApplicabilityConditions = <
  TKey extends string,
  TConditions extends
    ChargeItemDefinitionApplicabilityConditions<TKey> = ChargeItemDefinitionApplicabilityConditions<TKey>,
>(
  applicability: fhir4.ChargeItemDefinitionApplicability[],
): Partial<TConditions> =>
  applicability.reduce<Partial<TConditions>>((acc, current) => {
    const parsed = parseApplicabilityExpression<TKey>(current);

    if (!parsed) {
      return acc;
    }

    const [key, value] = parsed;

    return {
      ...acc,
      [key]: value,
    };
  }, {});
